import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';

const PRO_FEATURES = [
  'Workout Tracker (1,300+ exercises with GIF demos)',
  'Muscle Explorer (interactive anatomy map + workouts)',
  'Nutrition Tracker (600k+ foods + barcode scanner)',
  'Supplement Tracker (USDA database)',
  'Medication Tracker (OpenFDA drug database)',
  'Progress Dashboard (charts, streaks, trends)',
];

const FREE_FEATURES = [
  'Weight Tracker',
  'Health Vitals (blood pressure logging)',
  'Dashboard overview',
];

export default function UpgradePrompt({ featureName = 'This feature' }) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: user.uid,
          email: user.email,
          successUrl: `${window.location.origin}/dashboard`,
          cancelUrl: window.location.href,
        }),
      });
      const data = await res.json();
      if (!data.url) throw new Error(data.error || 'No checkout URL returned');
      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '70vh',
      padding: '40px 20px',
    }}>
      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: '16px',
        padding: '40px 36px',
        maxWidth: '460px',
        width: '100%',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '52px', marginBottom: '12px' }}>🔒</div>

        <h2 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '26px',
          fontWeight: '700',
          color: theme.accent,
          margin: '0 0 6px 0',
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
        }}>
          Pro Feature
        </h2>

        <p style={{ color: theme.text, fontSize: '15px', margin: '0 0 24px 0' }}>
          <strong>{featureName}</strong> requires VitalForge Pro.
        </p>

        <div style={{
          background: theme.bgSecondary,
          border: `1px solid ${theme.accent}`,
          borderRadius: '10px',
          padding: '14px 20px',
          marginBottom: '24px',
          display: 'inline-block',
          minWidth: '140px',
        }}>
          <div style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '38px',
            fontWeight: '700',
            color: theme.accent,
            lineHeight: 1,
          }}>
            $9.99
          </div>
          <div style={{ color: theme.textSecondary, fontSize: '13px', marginTop: '2px' }}>
            per month
          </div>
        </div>

        <div style={{ textAlign: 'left', marginBottom: '28px' }}>
          <p style={{ color: theme.textSecondary, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px 0' }}>
            Unlocks with Pro
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px 0' }}>
            {PRO_FEATURES.map((f) => (
              <li key={f} style={{ color: theme.text, fontSize: '14px', padding: '4px 0', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ color: theme.accent, flexShrink: 0 }}>✦</span> {f}
              </li>
            ))}
          </ul>

          <p style={{ color: theme.textSecondary, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 8px 0' }}>
            Always free
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {FREE_FEATURES.map((f) => (
              <li key={f} style={{ color: theme.textSecondary, fontSize: '14px', padding: '4px 0', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ flexShrink: 0 }}>✓</span> {f}
              </li>
            ))}
          </ul>
        </div>

        <button
          onClick={handleUpgrade}
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px',
            background: loading ? theme.border : theme.accent,
            color: '#000',
            border: 'none',
            borderRadius: '8px',
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '16px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'opacity 0.2s',
          }}
        >
          {loading ? 'Opening Checkout…' : 'Upgrade to Pro — $9.99/mo'}
        </button>

        <p style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '12px', lineHeight: '1.5' }}>
          Cancel anytime. Secure payment via Stripe.
        </p>
      </div>
    </div>
  );
}
